import type { ComponentType, ReactNode, SVGProps } from 'react';
import type { NavigationPageItem } from '../types/navigation';
import { AppWindow, Database, Folder, Route as RouteIcon } from 'lucide-react';

type LucideIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

import type {
  ModuleItem,
  EntityLeaf,
  AppLeaf,
  FrontendRouteLeaf,
  NavNode,
} from '../api/endpoints/useModules';
import type { Condition } from '@locus-taxy/encore-grid';
import { RoutePath } from '../types/categories';
import { buildClientPrefixedPath } from './url';
import { navBadgeKey } from './navBadge';

interface NavigationBuildOptions {
  clientId: string;
  parentSegments: string[];
  parentLabels: string[];
  leaves: ModuleLeafRoute[];
  /** Track group nodes by their computed path to merge duplicates without altering URLs. */
  groupRegistry: Map<string, NavigationPageItem>;
  /** Track full paths to avoid pushing duplicate navigation items. */
  pathSet: Set<string>;
  /** Localized fallback title for frontend-route leaves that have no label. */
  frontendRouteFallbackTitle: string;
}

const DEFAULT_FRONTEND_ROUTE_TITLE = 'Frontend Route';

export interface ModuleLeafRoute {
  type: 'ENTITY' | 'APP' | 'FRONTEND_ROUTE';
  /**
   * Ordered list of ID segments that compose the full path for this leaf.
   * The last entry is the leaf's own identifier used in the URL.
   */
  pathSegments: string[];
  /** Name of the dynamic segment for the leaf in the route definition. */
  paramName: 'entityName' | 'appName' | 'routePath';
  label: string;
  /** The concrete value that will appear in the URL for the leaf node. */
  leafSegment: string;
  entityName?: string;
  /** Usecase slug (DP-3570) for an ENTITY leaf targeting a usecase page. */
  usecase?: string;
  appName?: string;
  route?: string;
  /** ENTITY only (DP-6112) — this leaf renders a record count badge. */
  showCount?: boolean;
  /** ENTITY only (DP-6112) — the condition that count is taken over. */
  countCondition?: Condition;
  /** ENTITY only (DP-6112) — seconds between re-counts; absent = count once. */
  countRefetchInterval?: number;
  /**
   * Display labels of ancestor navigation groups for this leaf, in order from root to parent.
   * Used for DOM-independent active-parent highlighting when descendants are collapsed.
   */
  ancestorLabels: string[];
  /**
   * The full path as expected by Toolpad's internal router (including group segments and backtracking).
   * Used to force active state highlighting when the real URL is flat.
   */
  toolpadPath: string;
}

export interface ModulesNavigationResult {
  navigation: NavigationPageItem[];
  leaves: ModuleLeafRoute[];
  /** Map of path segment chains (joined by '/') to their display labels for nav nodes. */
  pathLabelMap: Record<string, string>;
}

export function buildModulesNavigation(
  items: ModuleItem[] | undefined,
  clientId: string,
  frontendRouteFallbackTitle: string = DEFAULT_FRONTEND_ROUTE_TITLE
): ModulesNavigationResult {
  const leaves: ModuleLeafRoute[] = [];
  const pathLabelMap: Record<string, string> = {};
  const navigation = buildNavigationItems(items ?? [], {
    clientId,
    parentSegments: [],
    parentLabels: [],
    leaves,
    groupRegistry: new Map(),
    pathSet: new Set(),
    pathLabelMap,
    frontendRouteFallbackTitle,
  });

  return { navigation, leaves, pathLabelMap };
}

export function findFirstLeafPath(leaves: ModuleLeafRoute[]): string | null {
  if (!leaves.length) {
    return null;
  }

  return buildPathFromSegments(leaves[0].pathSegments);
}

function renderLucide(Icon: LucideIcon) {
  return <Icon className="size-4" />;
}

function getBacktrackPrefix(depth: number): string {
  return depth > 0 ? '../'.repeat(depth) : '';
}

function buildNavigationItems(
  items: ModuleItem[],
  options: NavigationBuildOptions & { pathLabelMap: Record<string, string> }
): NavigationPageItem[] {
  const {
    clientId,
    parentSegments,
    parentLabels,
    leaves,
    pathLabelMap,
    groupRegistry,
    pathSet,
    frontendRouteFallbackTitle,
  } = options;
  const result: NavigationPageItem[] = [];

  const addLeafNode = (
    targetPath: string,
    title: string,
    icon: string | undefined,
    defaultIcon: ReactNode,
    leafData: Omit<ModuleLeafRoute, 'toolpadPath'>
  ) => {
    // Check global uniqueness based on the target URL
    if (pathSet.has(targetPath)) {
      return;
    }
    pathSet.add(targetPath);

    // Construct segment: backtrack out of parent groups + target path (without leading slash)
    // e.g. parents=['ops'], target='/clientId/...' -> '../clientId/...'
    const backtrack = getBacktrackPrefix(parentSegments.length);
    const segment = `${backtrack}${targetPath.slice(1)}`;

    // Calculate the Toolpad-internal path by joining parent segments and the leaf segment
    // Note: parentSegments contains the group labels/IDs used as segments
    const toolpadPath = buildPathFromSegments([...parentSegments, segment]);

    result.push({
      segment,
      title,
      icon: defaultIcon,
      iconName: icon,
      // Identity only — the number is fetched separately and applied by
      // `applyNavBadges`, which keeps this build independent of the network.
      ...(leafData.showCount &&
        leafData.entityName && {
          badgeKey: navBadgeKey(leafData.entityName, leafData.countCondition),
        }),
    });

    leaves.push({
      ...leafData,
      toolpadPath,
    });
  };

  items.forEach(item => {
    if (isNavNode(item)) {
      processNavGroupItem(item, {
        clientId,
        parentSegments,
        parentLabels,
        leaves,
        groupRegistry,
        pathSet,
        pathLabelMap,
        frontendRouteFallbackTitle,
        result,
      });
      return;
    }

    if (isEntityLeaf(item)) {
      processEntityLeaf(item, clientId, parentLabels, addLeafNode);
      return;
    }

    if (isAppLeaf(item)) {
      processAppLeaf(item, clientId, parentLabels, addLeafNode);
      return;
    }

    if (isFrontendRouteLeaf(item)) {
      processFrontendRouteLeaf(
        item,
        clientId,
        parentLabels,
        addLeafNode,
        frontendRouteFallbackTitle
      );
    }
  });

  return result;
}

type AddLeafNode = (
  targetPath: string,
  title: string,
  icon: string | undefined,
  defaultIcon: ReactNode,
  leafData: Omit<ModuleLeafRoute, 'toolpadPath'>
) => void;

function processNavGroupItem(
  item: NavNode,
  ctx: NavigationBuildOptions & {
    pathLabelMap: Record<string, string>;
    result: NavigationPageItem[];
  }
): void {
  const {
    clientId,
    parentSegments,
    parentLabels,
    leaves,
    groupRegistry,
    pathSet,
    pathLabelMap,
    frontendRouteFallbackTitle,
    result,
  } = ctx;

  const navKeySegment = normalizeSegment(item.id ?? item.key ?? item.label);
  const keySegments = navKeySegment
    ? appendSegment(parentSegments, navKeySegment)
    : parentSegments.slice();
  const fullPath = buildPathFromSegments(keySegments);
  const nextLabels = [...parentLabels, item.label];
  // Record the label for this path depth to rebuild breadcrumbs later
  const key = keySegments.join('/');
  if (key) {
    pathLabelMap[key] = item.label;
  }
  const children = buildNavigationItems(item.items ?? [], {
    clientId,
    parentSegments: keySegments,
    parentLabels: nextLabels,
    leaves,
    groupRegistry,
    pathSet,
    pathLabelMap,
    frontendRouteFallbackTitle,
  });

  const existingGroup = groupRegistry.get(fullPath);
  if (existingGroup) {
    existingGroup.children = [...(existingGroup.children ?? []), ...children];
    return;
  }
  if (children.length === 0) {
    // No existing group and no children to register
    return;
  }
  // Use the label/id as segment to ensure uniqueness and prevent "Duplicate path" warnings.
  // Child items will use "../" prefixes to backtrack out of these group segments
  // so that the final URL matches the flat route structure.
  const groupItem: NavigationPageItem = {
    segment: navKeySegment ?? item.label,
    title: item.label,
    icon: renderLucide(Folder),
    iconName: item.icon,
    children,
  };
  groupRegistry.set(fullPath, groupItem);
  result.push(groupItem);
}

function processEntityLeaf(
  item: EntityLeaf,
  clientId: string,
  parentLabels: string[],
  addLeafNode: AddLeafNode
): void {
  // Usecase leaves (DP-3570) point at `/entities/{entityName}/uc/{usecase}`.
  // The extra `uc/{usecase}` path segments give the leaf a distinct URL from
  // both the default entity leaf and other usecases of the same entity, so
  // the existing global-uniqueness de-dupe and prefix-based active-state
  // highlighting keep working with no special-casing (leaf href = page URL).
  const entitiesSegment = RoutePath.ENTITIES.replace(/^\//, '');
  const entityPath = item.usecase
    ? `${RoutePath.ENTITIES}/${item.entityName}/uc/${item.usecase}`
    : `${RoutePath.ENTITIES}/${item.entityName}`;
  const targetPath = buildClientPrefixedPath(entityPath, clientId);
  const title = item.label ?? item.entityName;
  const pathSegments = item.usecase
    ? [entitiesSegment, item.entityName, 'uc', item.usecase]
    : [entitiesSegment, item.entityName];

  addLeafNode(targetPath, title, item.icon, renderLucide(Database), {
    type: 'ENTITY',
    pathSegments,
    paramName: 'entityName',
    label: title,
    leafSegment: item.usecase ?? item.entityName,
    entityName: item.entityName,
    usecase: item.usecase,
    showCount: item.showCount,
    countCondition: item.countCondition,
    countRefetchInterval: item.countRefetchInterval,
    ancestorLabels: parentLabels.slice(),
  });
}

function processAppLeaf(
  item: AppLeaf,
  clientId: string,
  parentLabels: string[],
  addLeafNode: AddLeafNode
): void {
  const appPath = `${RoutePath.APPS}/${item.appName}`;
  const targetPath = buildClientPrefixedPath(appPath, clientId);
  const title = item.appOverrides?.name ?? item.label ?? item.appName;

  addLeafNode(targetPath, title, item.icon, renderLucide(AppWindow), {
    type: 'APP',
    pathSegments: [RoutePath.APPS.replace(/^\//, ''), item.appName],
    paramName: 'appName',
    label: title,
    leafSegment: item.appName,
    appName: item.appName,
    ancestorLabels: parentLabels.slice(),
  });
}

function processFrontendRouteLeaf(
  item: FrontendRouteLeaf,
  clientId: string,
  parentLabels: string[],
  addLeafNode: AddLeafNode,
  fallbackTitle: string
): void {
  const normalizedLeaf = item.route;
  const title = item.label ?? fallbackTitle;
  const targetPath = buildClientPrefixedPath(item.route, clientId);

  addLeafNode(targetPath, title, item.icon, renderLucide(RouteIcon), {
    type: 'FRONTEND_ROUTE',
    pathSegments: [targetPath.slice(1)],
    paramName: 'routePath',
    label: title,
    leafSegment: normalizedLeaf,
    route: item.route,
    ancestorLabels: parentLabels.slice(),
  });
}

function isNavNode(item: ModuleItem): item is NavNode {
  return Boolean((item as NavNode).items);
}

function isEntityLeaf(item: ModuleItem): item is EntityLeaf {
  return (item as EntityLeaf).type === 'ENTITY';
}

function isAppLeaf(item: ModuleItem): item is AppLeaf {
  return (item as AppLeaf).type === 'APP';
}

function isFrontendRouteLeaf(item: ModuleItem): item is FrontendRouteLeaf {
  return (item as FrontendRouteLeaf).type === 'FRONTEND_ROUTE';
}

function appendSegment(
  segments: string[],
  candidate?: string | null
): string[] {
  const normalized = normalizeSegment(candidate);
  if (!normalized) {
    return segments.slice();
  }
  return [...segments, normalized];
}

/**
 * Strips leading/trailing '/' characters without regex (avoids the
 * backtracking-hotspot heuristic static analyzers flag on quantified
 * slash patterns applied to path-like strings).
 */
function stripSlashes(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && value[start] === '/') {
    start++;
  }
  while (end > start && value[end - 1] === '/') {
    end--;
  }
  return value.slice(start, end);
}

function normalizeSegment(segment?: string | null): string | null {
  if (!segment) {
    return null;
  }

  const trimmed = stripSlashes(segment.trim());
  if (!trimmed) {
    return null;
  }

  return trimmed;
}

function buildPathFromSegments(segments: string[]): string {
  if (!segments.length) {
    return '/';
  }

  return `/${segments.join('/')}`;
}

/**
 * Strip the optional `/clientId/:clientId` prefix from a pathname.
 */
export function stripClientPrefix(pathname: string): string {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return path.replace(/^\/clientId\/[A-Za-z0-9_-]+(\/|$)/, '/');
}

/**
 * Find the best matching leaf (longest prefix match) for a given clientless path.
 */
export function findMatchingLeaf(
  pathWithoutClient: string,
  leaves: ModuleLeafRoute[]
): ModuleLeafRoute | null {
  let best: ModuleLeafRoute | null = null;
  let bestLen = -1;
  for (const leaf of leaves) {
    const prefix = `/${leaf.pathSegments.join('/')}`;
    if (
      pathWithoutClient === prefix ||
      pathWithoutClient.startsWith(`${prefix}/`)
    ) {
      if (leaf.pathSegments.length > bestLen) {
        best = leaf;
        bestLen = leaf.pathSegments.length;
      }
    }
  }
  return best;
}

export interface BreadcrumbItem {
  title: string;
}

/**
 * Build breadcrumb titles from the modules navigation for the current pathname.
 * This returns only titles, suitable for PageContainer.breadcrumbs.
 * Uses the module hierarchy (ancestorLabels) instead of URL path segments.
 */
export function buildBreadcrumbsFromModules(
  pathname: string,
  leaves: ModuleLeafRoute[]
): BreadcrumbItem[] {
  const clientless = stripClientPrefix(pathname);
  const match = findMatchingLeaf(clientless, leaves);
  if (!match) {
    return [];
  }

  // Use ancestorLabels (module hierarchy) + leaf label for breadcrumbs
  const items: BreadcrumbItem[] = match.ancestorLabels.map(label => ({
    title: label,
  }));
  items.push({ title: match.label });

  return items;
}
